import { useState, useRef, useEffect } from 'react';
import { Box, Button, Group, Text, Stack, Progress, ActionIcon, Alert } from '@mantine/core';
import {
	IconUpload,
	IconTrash,
	IconPlayerPlay,
	IconMusic,
	IconAlertCircle,
} from '@tabler/icons-react';
import {
	useGenerateMediaUploadUrlMutation,
	useDeleteMediaFileMutation,
	ExerciseTypeEnum,
} from '@codezo/graphql';

interface MediaUploaderProps {
	exerciseType: 'AUDIO' | 'VIDEO';
	language: string; // Required: which language this upload is for
	value?: {
		url: string;
		duration: number;
		fileId?: string;
	};
	onChange: (value: { url: string; duration: number; fileId: string } | null) => void;
	onFileIdChange?: (fileId: string | null) => void; // Track fileId for cleanup
}

const ACCEPT_TYPES = {
	AUDIO: 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/mpeg',
	VIDEO: 'video/mp4,video/quicktime,video/webm,video/avi',
};

const MAX_FILE_SIZE = {
	AUDIO: 10 * 1024 * 1024 * 1024, // 10GB - effectively unlimited (streams directly to Azure)
	VIDEO: 10 * 1024 * 1024 * 1024, // 10GB - effectively unlimited (streams directly to Azure)
};

export function MediaUploader({
	exerciseType,
	language,
	value,
	onChange,
	onFileIdChange,
}: MediaUploaderProps) {
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);

	const [generateUploadUrl] = useGenerateMediaUploadUrlMutation();
	const [deleteMediaFile] = useDeleteMediaFileMutation();

	const isVideo = exerciseType === 'VIDEO';
	const maxSize = MAX_FILE_SIZE[exerciseType];
	const accept = ACCEPT_TYPES[exerciseType];

	// Extract duration from local file (before upload)
	const extractDurationFromFile = (file: File): Promise<number> => {
		return new Promise((resolve, reject) => {
			const mediaElement = isVideo
				? document.createElement('video')
				: document.createElement('audio');

			const objectUrl = URL.createObjectURL(file);

			mediaElement.addEventListener(
				'loadedmetadata',
				() => {
					const duration = Math.round(mediaElement.duration);
					URL.revokeObjectURL(objectUrl); // Clean up
					resolve(duration);
				},
				{ once: true }
			);

			mediaElement.addEventListener(
				'error',
				(e) => {
					URL.revokeObjectURL(objectUrl); // Clean up
					console.error('Media loading error:', e);
					reject(new Error('Failed to extract duration from file'));
				},
				{ once: true }
			);

			mediaElement.src = objectUrl;
		});
	};

	const handleFileSelect = async (file: File) => {
		setError(null);

		// Validate file size with better error message
		if (file.size > maxSize) {
			const sizeMB = Math.round((file.size / 1024 / 1024) * 100) / 100;
			const maxSizeMB = Math.round(maxSize / 1024 / 1024);
			setError(`File size (${sizeMB}MB) exceeds maximum allowed size (${maxSizeMB}MB)`);
			return;
		}

		// Validate file type
		if (!file.type.startsWith(exerciseType.toLowerCase() + '/')) {
			setError(`Invalid file type. Expected ${exerciseType.toLowerCase()} file.`);
			return;
		}

		try {
			setUploading(true);
			setProgress(0);

			// Step 1: Extract duration from local file FIRST (no CORS issues)
			const duration = await extractDurationFromFile(file);

			// Step 2: Generate upload URL (with language)
			const { data } = await generateUploadUrl({
				variables: {
					input: {
						exerciseType: exerciseType as ExerciseTypeEnum,
						fileName: file.name,
						fileSize: file.size,
						language,
					},
				},
			});

			if (!data?.generateMediaUploadUrl) {
				throw new Error('Failed to generate upload URL');
			}

			const { uploadUrl, blobUrl, fileId } = data.generateMediaUploadUrl;

			// Track fileId for cleanup
			if (onFileIdChange) {
				onFileIdChange(fileId);
			}

			// Step 3: Upload file directly to Azure
			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener('progress', (e) => {
				if (e.lengthComputable) {
					const percentComplete = Math.round((e.loaded / e.total) * 100);
					setProgress(percentComplete);
				}
			});

			await new Promise<void>((resolve, reject) => {
				xhr.addEventListener('load', () => {
					if (xhr.status >= 200 && xhr.status < 300) {
						resolve();
					} else {
						reject(new Error(`Upload failed with status ${xhr.status}`));
					}
				});

				xhr.addEventListener('error', () => {
					reject(new Error('Upload failed'));
				});

				xhr.open('PUT', uploadUrl);
				xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
				xhr.setRequestHeader('Content-Type', file.type);
				xhr.send(file);
			});

			// Step 4: Update form value with Azure URL and extracted duration
			onChange({
				url: blobUrl,
				duration,
				fileId,
			});

			setUploading(false);
			setProgress(100);
		} catch (err: unknown) {
			console.error('Upload error:', err);

			// Extract user-friendly error message
			let errorMessage = 'Upload failed';
			const graphQLError = err as { graphQLErrors?: { message: string }[] };
			if (graphQLError?.graphQLErrors?.length && graphQLError.graphQLErrors.length > 0) {
				errorMessage = graphQLError.graphQLErrors[0].message;
			} else if (err instanceof Error) {
				errorMessage = err.message;
			}

			setError(errorMessage);
			setUploading(false);
			setProgress(0);
		}
	};

	const handleRemove = async () => {
		if (!value?.url) return;

		try {
			// Delete from Azure
			await deleteMediaFile({
				variables: {
					blobUrl: value.url,
				},
			});

			// Clear fileId tracking
			if (onFileIdChange && value.fileId) {
				onFileIdChange(null);
			}

			// Clear form value
			onChange(null);
		} catch (err) {
			console.error('Delete error:', err);
			setError('Failed to delete file');
		}
	};

	const formatDuration = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	const formatFileSize = (bytes: number): string => {
		const mb = bytes / 1024 / 1024;
		return `${mb.toFixed(2)} MB`;
	};

	return (
		<Stack gap="md">
			{error && (
				<Alert
					icon={<IconAlertCircle size={16} />}
					title="Error"
					color="red"
					onClose={() => setError(null)}
				>
					{error}
				</Alert>
			)}

			{!value && !uploading && (
				<Box
					style={{
						border: '2px dashed #dee2e6',
						borderRadius: '8px',
						padding: '2rem',
						textAlign: 'center',
						cursor: 'pointer',
						transition: 'border-color 0.2s',
					}}
					onClick={() => fileInputRef.current?.click()}
					onDragOver={(e) => {
						e.preventDefault();
						e.currentTarget.style.borderColor = '#228be6';
					}}
					onDragLeave={(e) => {
						e.currentTarget.style.borderColor = '#dee2e6';
					}}
					onDrop={(e) => {
						e.preventDefault();
						e.currentTarget.style.borderColor = '#dee2e6';
						const file = e.dataTransfer.files[0];
						if (file) handleFileSelect(file);
					}}
				>
					<Stack gap="xs" align="center">
						<IconUpload size={32} color="#228be6" />
						<Text fw={500}>
							Drop {isVideo ? 'video' : 'audio'} file here or click to browse
						</Text>
						<Text size="sm" c="dimmed">
							Max size: {maxSize / 1024 / 1024 / 1024}GB
						</Text>
						<Text size="xs" c="dimmed">
							Supported formats:{' '}
							{isVideo ? 'MP4, MOV, WebM, AVI' : 'MP3, WAV, M4A, AAC'}
						</Text>
					</Stack>
				</Box>
			)}

			{uploading && (
				<Box>
					<Text size="sm" mb="xs">
						Uploading... {progress}%
					</Text>
					<Progress value={progress} animated />
				</Box>
			)}

			{value && !uploading && (
				<Box
					style={{
						border: '1px solid #dee2e6',
						borderRadius: '8px',
						padding: '1rem',
					}}
				>
					<Group justify="space-between" mb="md">
						<Group gap="xs">
							{isVideo ? <IconPlayerPlay size={20} /> : <IconMusic size={20} />}
							<Text fw={500}>{isVideo ? 'Video' : 'Audio'} uploaded</Text>
						</Group>
						<ActionIcon color="red" variant="subtle" onClick={handleRemove}>
							<IconTrash size={18} />
						</ActionIcon>
					</Group>

					{/* Preview */}
					{isVideo ? (
						<video
							ref={mediaRef as React.RefObject<HTMLVideoElement>}
							src={value.url}
							controls
							style={{
								width: '100%',
								maxHeight: '300px',
								borderRadius: '4px',
								marginBottom: '0.5rem',
							}}
						/>
					) : (
						<audio
							ref={mediaRef as React.RefObject<HTMLAudioElement>}
							src={value.url}
							controls
							style={{
								width: '100%',
								marginBottom: '0.5rem',
							}}
						/>
					)}

					{/* Metadata */}
					<Group gap="md">
						<Text size="sm" c="dimmed">
							Duration: {formatDuration(value.duration)}
						</Text>
					</Group>
				</Box>
			)}

			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				style={{ display: 'none' }}
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFileSelect(file);
				}}
			/>
		</Stack>
	);
}
